/**
 * Project-wide timestamp contract
 * ================================
 * Backend timestamps are UTC instants (ISO 8601, since 2026-07 always with
 * 'Z' — older cached payloads may lack it). NEVER parse backend timestamps
 * with raw `new Date(string)`; always use these helpers — a naive ISO string
 * would otherwise be parsed as LOCAL time, shifting everything by 1-2h
 * (CET/CEST).
 *
 * `week_start` values (compliance tables) = Monday 00:00 Europe/Berlin,
 * serialized as the corresponding UTC instant (Sun 22:00/23:00Z). Use
 * {@link weekStartDisplayDate} / {@link formatWeekLabel} for display and
 * {@link getCurrentWeekStartBerlinIso} to build the current week's key.
 */

/**
 * Parse a backend timestamp string as a UTC instant. Appends 'Z' when no
 * offset is present; idempotent — safe with or without 'Z'.
 */
export function parseUtcTimestamp(ts: string): Date {
  return new Date(ts.endsWith('Z') || ts.includes('+') ? ts : ts + 'Z');
}

/** Epoch milliseconds variant of {@link parseUtcTimestamp}. */
export function parseUtcTimestampMs(ts: string): number {
  return parseUtcTimestamp(ts).getTime();
}

/**
 * Display anchor for a compliance `week_start` timestamp.
 *
 * `week_start` is Monday 00:00 Europe/Berlin serialized as UTC
 * (Sun 22:00/23:00Z). Parsing it as UTC and shifting to Monday noon makes
 * `toLocaleDateString` render the correct Monday regardless of ±1h DST offset.
 */
export function weekStartDisplayDate(weekStart: string): Date {
  return new Date(parseUtcTimestamp(weekStart).getTime() + 12 * 3600000);
}

/** ISO-8601 week number (week containing the first Thursday of the year is KW 1). */
export function isoWeekNumber(d: Date): number {
  const thursday = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  thursday.setDate(thursday.getDate() - ((thursday.getDay() + 6) % 7) + 3);
  const firstThursday = new Date(thursday.getFullYear(), 0, 4);
  firstThursday.setDate(firstThursday.getDate() - ((firstThursday.getDay() + 6) % 7) + 3);
  return 1 + Math.round((thursday.getTime() - firstThursday.getTime()) / (7 * 86400000));
}

/**
 * ISO week-numbering year of a date (the year the ISO week belongs to — for
 * 29.12.–03.01. weeks this differs from the calendar year of the Monday).
 */
export function isoWeekYear(d: Date): number {
  const thursday = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  thursday.setDate(thursday.getDate() - ((thursday.getDay() + 6) % 7) + 3);
  return thursday.getFullYear();
}

/**
 * Shared label for a compliance week, e.g. "KW 27/2026 (29.06. – 05.07.)".
 * Built on {@link weekStartDisplayDate} (noon-anchored Monday), so the shown
 * dates are DST-safe. Week end = start + 6 days. The year is part of the
 * label: multi-year archives contain the same KW twice.
 */
export function formatWeekLabel(weekStartIso: string): string {
  const monday = weekStartDisplayDate(weekStartIso);
  const sunday = new Date(monday.getTime() + 6 * 86400000);
  const fmt = (dt: Date) => dt.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  return `KW ${isoWeekNumber(monday)}/${isoWeekYear(monday)} (${fmt(monday)} – ${fmt(sunday)})`;
}

// ── Europe/Berlin wall-clock helpers ─────────────────────────────────────

const berlinPartsFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Europe/Berlin',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

interface WallClockParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function getBerlinParts(instant: Date): WallClockParts {
  const parts: Partial<WallClockParts> = {};
  for (const p of berlinPartsFormatter.formatToParts(instant)) {
    if (p.type === 'year' || p.type === 'month' || p.type === 'day'
      || p.type === 'hour' || p.type === 'minute' || p.type === 'second') {
      parts[p.type] = parseInt(p.value, 10);
    }
  }
  return parts as WallClockParts;
}

/** Convert a Europe/Berlin wall-clock time to the corresponding UTC instant. */
function berlinWallClockToUtc(year: number, month: number, day: number, hour = 0, minute = 0): Date {
  const targetAsUtc = Date.UTC(year, month - 1, day, hour, minute);
  let guess = targetAsUtc;
  // Two iterations converge for any fixed-offset window (DST transitions included).
  for (let i = 0; i < 2; i++) {
    const p = getBerlinParts(new Date(guess));
    const guessAsWallClock = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    guess += targetAsUtc - guessAsWallClock;
  }
  return new Date(guess);
}

/**
 * Current compliance week key: Monday 00:00 Europe/Berlin as UTC-instant ISO
 * string (Sun 22:00/23:00Z) — matches the backend `week_start` convention.
 * Does NOT assume the browser runs in the Berlin timezone.
 */
export function getCurrentWeekStartBerlinIso(): string {
  const nowBerlin = getBerlinParts(new Date());
  // Noon-anchored UTC date carrying the Berlin calendar date → safe weekday math.
  const berlinDateAnchor = new Date(Date.UTC(nowBerlin.year, nowBerlin.month - 1, nowBerlin.day, 12));
  const mondayOffset = (berlinDateAnchor.getUTCDay() + 6) % 7;
  const monday = new Date(berlinDateAnchor.getTime() - mondayOffset * 86400000);
  return berlinWallClockToUtc(
    monday.getUTCFullYear(),
    monday.getUTCMonth() + 1,
    monday.getUTCDate(),
  ).toISOString();
}

/**
 * "20.09., 14:30 Uhr" — compact display of a measurement/calculation instant
 * in the browser's locale time; null when the date is missing or invalid.
 */
export function formatInstantLabel(d: Date | null | undefined): string | null {
  if (!d || Number.isNaN(d.getTime())) return null;
  return `${d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}, ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr`;
}

/**
 * End of the Europe/Berlin calendar day that contains the instant (= the next
 * Berlin midnight) as UTC ISO string. Lets a view that asks for whole days keep
 * one request while the user scrubs within that day.
 */
export function berlinDayEndUtcIso(instant: string | Date): string {
  const d = typeof instant === 'string' ? parseUtcTimestamp(instant) : instant;
  const p = getBerlinParts(d);
  const nextDay = new Date(Date.UTC(p.year, p.month - 1, p.day + 1, 12));
  return berlinWallClockToUtc(nextDay.getUTCFullYear(), nextDay.getUTCMonth() + 1, nextDay.getUTCDate()).toISOString();
}

/** True when both instants fall on the same Europe/Berlin calendar day. */
export function isSameBerlinDay(a: Date, b: Date): boolean {
  const pa = getBerlinParts(a);
  const pb = getBerlinParts(b);
  return pa.year === pb.year && pa.month === pb.month && pa.day === pb.day;
}

/**
 * German display of a backend instant in Europe/Berlin — for calendar values
 * (Stichtage, day bars) that the backend cut at a Berlin midnight and that must
 * not shift to another day in a browser outside Germany.
 */
export function formatBerlin(instant: string, options: Intl.DateTimeFormatOptions): string {
  return parseUtcTimestamp(instant).toLocaleString('de-DE', { ...options, timeZone: 'Europe/Berlin' });
}
